import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useParams, useNavigate } from 'react-router-dom';
import { useUser } from '../Context/UserContext';
import { PERMISSIONS } from '../Utils/permissions';
import { openOrderWhatsAppEg } from '../Utils/whatsapp';
import './Admin.css';

export default function OrderDetails() {
  const { orderId } = useParams();
  const { permissions, role } = useUser();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const openWhatsApp = () => {
    const res = openOrderWhatsAppEg(order);
    if (!res?.ok) {
      alert('لا يوجد رقم هاتف للعميل');
    }
  };

  const canManageOrders = !!permissions?.[PERMISSIONS.ORDERS_UPDATE];

  useEffect(() => {
    fetchOrderDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const fetchOrderDetails = async () => {
    try {
      const orderDoc = doc(db, 'orders', orderId);
      const orderSnapshot = await getDoc(orderDoc);
      
      if (orderSnapshot.exists()) {
        setOrder({
          id: orderSnapshot.id,
          ...orderSnapshot.data()
        });
      } else {
        console.error('Order not found');
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (newStatus) => {
    try {
      const orderRef = doc(db, 'orders', orderId);
      await updateDoc(orderRef, { status: newStatus });
      setOrder(prev => ({ ...prev, status: newStatus }));
    } catch (error) {
      console.error('Error updating order status:', error);
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'pending': return '#f59e0b';
      case 'confirmed': return '#10b981';
      case 'processing': return '#3b82f6';
      case 'shipped': return '#8b5cf6';
      case 'delivered': return '#10b981';
      case 'cancelled': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getStatusBadge = (status) => {
    return (
      <span className="status-badge" style={{ backgroundColor: getStatusColor(status) }}>
        {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Pending'}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="admin-content">
        <div className="loading-spinner">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="admin-content">
        <div className="error-state">
          <ion-icon name="alert-circle-outline"></ion-icon>
          <h3>Order not found</h3>
          <button onClick={() => navigate('/admin/orders')} className="back-btn">
            Back to Orders
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button 
            onClick={() => navigate('/admin/orders')} 
            className="btn-secondary"
            style={{ padding: '8px 12px' }}
          >
            <ion-icon name="arrow-back-outline"></ion-icon>
          </button>
          <div>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              Order #{order.id.slice(-8)}
              {getStatusBadge(order.status)}
            </h2>
            <p>
              {order.createdAt?.toDate ? 
                new Date(order.createdAt.toDate()).toLocaleString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                }) : 
                new Date(order.createdAt).toLocaleString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })
              }
            </p>
          </div>
        </div>
        
        <div style={{ display: 'flex', gap: '12px' }}>
          {role !== 'shipping_company' && (
            <button
              type="button"
              className="btn-secondary"
              onClick={openWhatsApp}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <ion-icon name="logo-whatsapp"></ion-icon>
              Chat on WhatsApp
            </button>
          )}
          {canManageOrders && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ fontWeight: 600, color: '#202223' }}>Status:</label>
              <select 
                className="custom-select"
                style={{ width: 'auto', minWidth: '150px' }}
                value={order.status || 'pending'}
                onChange={(e) => updateOrderStatus(e.target.value)}
              >
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="processing">Processing</option>
                <option value="shipped">Shipped</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          )}
        </div>
      </div>

      <div className="form-grid">
        <div className="admin-card">
          <div className="card-header">
            <h3>Order Information</h3>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f1f2f3', paddingBottom: '12px' }}>
              <span style={{ color: '#6d7175' }}>Order ID</span>
              <span style={{ fontWeight: 500 }}>#{order.id}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f1f2f3', paddingBottom: '12px' }}>
              <span style={{ color: '#6d7175' }}>Payment Status</span>
              <span style={{ 
                fontWeight: 600, 
                color: order.paymentStatus === 'paid' ? '#03543f' : '#b98900',
                backgroundColor: order.paymentStatus === 'paid' ? '#def7ec' : '#fff4e5',
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '0.85rem'
              }}>
                {order.paymentStatus === 'paid' ? 'Paid' : 'Pending'}
              </span>
            </div>

            {(order.paymentMethod === 'vodafone_cash' || order.paymentMethod === 'instapay') && order.paymentEvidenceUrl && (
               <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f1f2f3', paddingBottom: '12px' }}>
                  <span style={{ color: '#6d7175' }}>Payment Evidence</span>
                  <a 
                    href={order.paymentEvidenceUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ 
                      color: '#008060', 
                      textDecoration: 'none', 
                      fontWeight: 500,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    <ion-icon name="image-outline"></ion-icon>
                    View Screenshot
                  </a>
               </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f1f2f3', paddingBottom: '12px' }}>
              <span style={{ color: '#6d7175' }}>Fulfillment Status</span>
              <span style={{ 
                fontWeight: 600, 
                color: order.status === 'delivered' ? '#03543f' : '#4b5563',
                backgroundColor: order.status === 'delivered' ? '#def7ec' : '#f3f4f6',
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '0.85rem'
              }}>
                {order.status === 'delivered' ? 'Fulfilled' : 'Unfulfilled'}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#6d7175' }}>Total Amount</span>
              <span style={{ fontWeight: 700, fontSize: '1.1rem' }}>EGP {order.totalAmount}</span>
            </div>
          </div>
        </div>

        <div className="admin-card">
          <div className="card-header">
            <h3>Customer Information</h3>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ 
                width: '40px', 
                height: '40px', 
                borderRadius: '50%', 
                backgroundColor: '#e3f1df', 
                color: '#008060', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                fontWeight: 600,
                fontSize: '1.2rem'
              }}>
                {order.customerName?.charAt(0).toUpperCase()}
              </div>
              <div>
                <div style={{ fontWeight: 600, color: '#202223' }}>{order.customerName}</div>
                <div style={{ fontSize: '0.9rem', color: '#6d7175' }}>Customer</div>
              </div>
            </div>
            
            <div style={{ marginTop: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', color: '#202223' }}>
                <ion-icon name="call-outline" style={{ color: '#6d7175' }}></ion-icon>
                <a href={`tel:${order.customerPhone}`} style={{ color: 'inherit', textDecoration: 'none' }}>{order.customerPhone}</a>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', color: '#202223' }}>
                <ion-icon name="mail-outline" style={{ color: '#6d7175' }}></ion-icon>
                <a href={`mailto:${order.customerEmail}`} style={{ color: 'inherit', textDecoration: 'none' }}>{order.customerEmail || 'No email provided'}</a>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', color: '#202223' }}>
                <ion-icon name="location-outline" style={{ color: '#6d7175', marginTop: '2px' }}></ion-icon>
                <span>{order.customerAddress || 'No address provided'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="admin-card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="card-header" style={{ padding: '20px', borderBottom: '1px solid #e1e3e5', marginBottom: 0 }}>
          <h3>Order Items</h3>
        </div>
        <div className="table-container" style={{ border: 'none', borderRadius: 0 }}>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Price</th>
                <th>Quantity</th>
                <th style={{ textAlign: 'right' }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {order.items?.map((item, index) => (
                <tr key={index}>
                  <td>
                    <div style={{ fontWeight: 500, color: '#202223' }}>{item.name}</div>
                    {item.category && <div style={{ fontSize: '0.85rem', color: '#6d7175' }}>{item.category}</div>}
                  </td>
                  <td>EGP {item.price}</td>
                  <td>{item.quantity}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>EGP {item.price * item.quantity}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        <div style={{ padding: '20px', backgroundColor: '#f9fafb', borderTop: '1px solid #e1e3e5' }}>
          <div style={{ maxWidth: '300px', marginLeft: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: '#6d7175' }}>
              <span>Subtotal</span>
              <span>EGP {order.subtotal || order.totalAmount}</span>
            </div>
            {order.shippingFee && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: '#6d7175' }}>
                <span>Shipping</span>
                <span>EGP {order.shippingFee}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e1e3e5', fontWeight: 700, fontSize: '1.1rem', color: '#202223' }}>
              <span>Total</span>
              <span>EGP {order.totalAmount}</span>
            </div>
          </div>
        </div>
      </div>

      {order.notes && (
        <div className="admin-card">
          <div className="card-header">
            <h3>Order Notes</h3>
          </div>
          <p style={{ color: '#6d7175', lineHeight: '1.5' }}>{order.notes}</p>
        </div>
      )}
    </div>
  );
}
